import { roundPhase, type Round } from './model';

/** Live cadence of the static short-market feed files. */
export const BASE_DELAY_MS = 5_000;
/** Slow cadence: rounds feed with no open round, candles of a round that starts later. */
export const IDLE_DELAY_MS = 30_000;
/** Upper bound of the error backoff. */
export const MAX_DELAY_MS = 60_000;
/** Candles of an upcoming round go back to the live cadence this close to its start. */
export const PRE_START_WINDOW_S = 60;
/** Candles of a round that ended this long ago and still 404 are never coming: stop asking. */
export const GIVE_UP_AFTER_END_S = 600;

export type PollState = { failures: number };
export const INITIAL_POLL_STATE: PollState = { failures: 0 };

/**
 * `cadence` overrides the base delay after a success; `final` ends the poll after this fetch.
 * A failure may carry `maxDelay` (do not back off past a deadline) and `final` (give up).
 */
export type ErrorPolicy = { maxDelay?: number; final?: boolean };
export type PollOutcome = { ok: true; cadence?: number; final?: boolean } | ({ ok: false } & ErrorPolicy);

/** 5s, 10s, 20s, 40s, then 60s for every further consecutive failure (404s included). */
export const backoffDelay = (failures: number) => Math.min(BASE_DELAY_MS * 2 ** Math.max(0, failures - 1), MAX_DELAY_MS);

/**
 * Next wait after a fetch, or `null` to stop polling. A success resets the backoff; a failure's
 * backoff is cut at its `maxDelay` (never below the base cadence, like the success-path caps).
 */
export const nextDelay = (state: PollState, outcome: PollOutcome): { state: PollState; delay: number | null } => {
  if (outcome.ok) return { state: INITIAL_POLL_STATE, delay: outcome.final ? null : (outcome.cadence ?? BASE_DELAY_MS) };
  const next = { failures: state.failures + 1 };
  if (outcome.final) return { state: next, delay: null };
  const cap = outcome.maxDelay === undefined ? Infinity : Math.max(BASE_DELAY_MS, outcome.maxDelay);
  return { state: next, delay: Math.min(backoffDelay(next.failures), cap) };
};

// Slow cadence, but never sleep past `deadline` (seconds) and never poll faster than the base.
const slowUntil = (deadline: number, now: number) =>
  Math.max(BASE_DELAY_MS, Math.min(IDLE_DELAY_MS, (deadline - now) * 1000));

/**
 * Rounds feed cadence: 5s while a round is open, otherwise 30s — but never sleep past the
 * start of the next scheduled round.
 */
const nextStart = (rounds: Round[], now: number) =>
  rounds
    .filter(r => r.status !== 'cancelled' && r.status !== 'resolved' && r.startAt > now)
    .reduce((min, r) => Math.min(min, r.startAt), Infinity);

export const roundsCadence = (rounds: Round[], now: number) =>
  rounds.some(r => roundPhase(r, now) === 'open') ? BASE_DELAY_MS : slowUntil(nextStart(rounds, now), now);

/** Rounds feed errors back off as usual, but never sleep past the next round start. */
export const roundsErrorPolicy = (rounds: Round[], now: number): ErrorPolicy => {
  const start = nextStart(rounds, now);
  return { maxDelay: start === Infinity ? undefined : (start - now) * 1000 };
};

/**
 * Candles cadence: an upcoming round more than 60s before its start only carries pre-round
 * context, so 30s; within 60s of the start, while open and until it resolves, 5s.
 */
export const candlesCadence = (round: Round | undefined, now: number) =>
  round && roundPhase(round, now) === 'upcoming' ? slowUntil(round.startAt - PRE_START_WINDOW_S, now) : BASE_DELAY_MS;

const httpStatus = (error: unknown) =>
  typeof error === 'object' && error !== null ? (error as { status?: unknown }).status : undefined;

/**
 * Candles errors: never sleep past T-60s of an upcoming round; give up on a cancelled round whose
 * file is missing (404/410) and on a file still missing (404) over 10 minutes after endAt.
 * Other errors on a cancelled round (5xx, offline) back off until a fetch succeeds, which is final.
 * A 404 before that only backs off: the engine may not have written the file yet.
 */
export const candlesErrorPolicy = (round: Round | undefined, now: number, error: unknown): ErrorPolicy => {
  if (!round) return {};
  const status = httpStatus(error);
  if (round.status === 'cancelled' && (status === 404 || status === 410)) return { final: true };
  if (now > round.endAt + GIVE_UP_AFTER_END_S && status === 404) return { final: true };
  const wake = round.startAt - PRE_START_WINDOW_S;
  return roundPhase(round, now) === 'upcoming' && wake > now ? { maxDelay: (wake - now) * 1000 } : {};
};

/** A resolved or cancelled round's candles no longer change: one fetch is enough. */
export const isFinalRound = (round?: Round) => round?.status === 'resolved' || round?.status === 'cancelled';
